/**
 * ScenariosPage — What-if simulator for real household decisions
 *
 * Runs concrete scenarios against the current household state:
 *   - Sell 35 Farm Avenue
 *   - Pay off The Gables mortgage early
 * and shows the impact on cashflow, FIRE status, and net worth.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, RefreshCw, ArrowRight, TrendingUp, AlertTriangle, Home, BookmarkCheck } from 'lucide-react'
import { clsx } from 'clsx'
import { api, updateHouseholdProfile } from '@/lib/api'
import ScenariosTabs from '@/components/ui/ScenariosTabs'

interface SellFarmAvenueResponse {
  scenario: string
  summary: string
  current_state: {
    property_gross_gbp: number
    mortgage_outstanding_gbp: number
    net_equity_gbp: number
    monthly_mortgage_interest_gbp: number
    annual_mortgage_interest_gbp: number
  }
  sale_mechanics: {
    sale_price_gbp: number
    purchase_price_gbp: number
    improvements_gbp: number
    cost_basis_gbp: number
    costs_pct: number
    sale_costs_gbp: number
    net_sale_proceeds_gbp: number
    mortgage_redeemed_gbp: number
    gross_gain_gbp: number
    computed_gain_gbp: number
    joint_ownership: boolean
    cgt_allowance_gbp: number
    ownership_start: string
    main_residence_end: string
    sale_date: string
    total_months_owned: number
    main_residence_months: number
    final_period_months: number
    prr_eligible_months: number
    chargeable_months: number
    prr_fraction_pct: number
    chargeable_fraction_pct: number
    prr_exempt_gain_gbp: number
    chargeable_gain_gbp: number
    taxable_gain_gbp: number
    cgt_rate_pct: number
    cgt_due_gbp: number
    cash_freed_before_cgt_gbp: number
    cash_after_cgt_gbp: number
  }
  household_impact: {
    net_worth_before_gbp: number
    net_worth_after_gbp: number
    net_worth_delta_gbp: number
    tracked_before_gbp: number
    tracked_after_gbp: number
    tracked_delta_gbp: number
  }
  fire_impact: {
    annual_spend_assumption_gbp: number
    multiple_before: number
    multiple_after: number
    multiple_delta: number
  }
  cashflow_impact: {
    mortgage_interest_saved_gbp_yr: number
    deployed_yield_pct: number
    extra_yield_on_freed_cash_gbp_yr: number
    total_cashflow_swing_gbp_yr: number
  }
  actions: string[]
}

interface SellGablesResponse {
  scenario: string
  summary: string
  current_state: {
    property_value_gbp: number
    mortgage_outstanding_gbp: number
    net_equity_gbp: number
    monthly_mortgage_gbp: number
    annual_mortgage_gbp: number
  }
  sale_mechanics: {
    sale_price_gbp: number
    costs_pct: number
    sale_costs_gbp: number
    net_sale_proceeds_gbp: number
    mortgage_redeemed_gbp: number
    prr_coverage_pct: number
    cgt_due_gbp: number
    equity_freed_gbp: number
    note: string
  }
  household_impact: {
    net_worth_before_gbp: number
    net_worth_after_gbp: number
    net_worth_delta_gbp: number
    tracked_before_gbp: number
    tracked_after_gbp: number
    tracked_delta_gbp: number
  }
  rent_abroad: {
    monthly_rent_gbp: number
    rent_year_1_gbp: number
    indexation_pct: number
    years: number
    lifetime_rent_gbp: number
    average_annual_rent_gbp: number
  }
  cashflow_over_horizon: {
    years: number
    yield_on_freed_equity_gbp: number
    mortgage_payments_saved_gbp: number
    rent_paid_gbp: number
    net_swing_gbp: number
  }
  recommendation: 'POSITIVE' | 'NEGATIVE'
  actions: string[]
}

interface PayOffGablesResponse {
  scenario: string
  summary: string
  current_state: {
    property_value_gbp: number
    outstanding_gbp: number
    rate_pct: number
    annual_interest_gbp: number
  }
  opportunity_cost: {
    market_real_return_pct: number
    annual_opportunity_cost_gbp: number
  }
  early_repayment_charge: {
    low_gbp: number
    high_gbp: number
    note: string
  }
  verdict: {
    net_annual_saving_gbp: number
    recommendation: string
    reasoning: string
  }
}

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

export default function ScenariosPage() {
  const [farmAvenue, setFarmAvenue] = useState<SellFarmAvenueResponse | null>(null)
  const [gablesRelocate, setGablesRelocate] = useState<SellGablesResponse | null>(null)
  const [gables, setGables] = useState<PayOffGablesResponse | null>(null)
  const [salePrice, setSalePrice] = useState(630_000)
  const [costsPct, setCostsPct] = useState(2.0)
  const [cgtGain, setCgtGain] = useState(0)
  const [purchasePrice, setPurchasePrice] = useState(412_500)  // Simon confirmed: Sept 2013 @ £412,500
  const [improvements, setImprovements] = useState(50_000)
  // Gables relocate sliders
  const [gablesSalePrice, setGablesSalePrice] = useState(1_600_000)
  const [gablesMonthlyRent, setGablesMonthlyRent] = useState(4_500)
  const [gablesYearsRenting, setGablesYearsRenting] = useState(20)
  const [gablesDeployedYield, setGablesDeployedYield] = useState(4.2)
  // Planning assumptions — single source of truth written back to household profile
  const [includeFarmAvenueSale, setIncludeFarmAvenueSale] = useState(true)
  const [farmAvenueSaleYear, setFarmAvenueSaleYear] = useState(2026)
  const [profileSaved, setProfileSaved] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Debounced profile writeback (same pattern as PlanningPage)
  const profileTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debouncedProfileUpdate = useCallback(
    (sell: boolean, year: number) => {
      if (profileTimer.current) clearTimeout(profileTimer.current)
      profileTimer.current = setTimeout(() => {
        updateHouseholdProfile({
          planning_assumptions: {
            sell_farm_avenue: sell,
            farm_avenue_sale_year: year,
          },
        })
          .then(() => {
            setProfileSaved(true)
            setTimeout(() => setProfileSaved(false), 2000)
          })
          .catch(() => {})
      }, 500)
    },
    [],
  )

  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const [fa, gr, g] = await Promise.all([
        api.get<SellFarmAvenueResponse>('/api/scenarios/sell-farm-avenue', {
          params: {
            sale_price_gbp: salePrice,
            purchase_price_gbp: purchasePrice,
            improvements_gbp: improvements,
            costs_pct: costsPct,
            capital_gain_gbp: cgtGain,
          },
        }),
        api.get<SellGablesResponse>('/api/scenarios/sell-gables-and-relocate', {
          params: {
            sale_price_gbp: gablesSalePrice,
            monthly_rent_abroad_gbp: gablesMonthlyRent,
            rent_indexation_pct: 3.0,
            years_renting: gablesYearsRenting,
            deployed_yield_pct: gablesDeployedYield,
          },
        }),
        api.get<PayOffGablesResponse>('/api/scenarios/pay-off-gables'),
      ])
      setFarmAvenue(fa.data)
      setGablesRelocate(gr.data)
      setGables(g.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load scenarios')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const t = setTimeout(fetchAll, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salePrice, costsPct, cgtGain, purchasePrice, improvements, gablesSalePrice, gablesMonthlyRent, gablesYearsRenting, gablesDeployedYield])

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Sparkles size={20} className="text-blue-light" aria-hidden />
            What-If Scenarios
          </h1>
          <p className="text-sm text-text-secondary">
            Concrete simulations against your real household state
          </p>
        </div>
        <button type="button" onClick={fetchAll} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      <ScenariosTabs />

      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5 text-sm text-rose-light">
          {error}
        </div>
      )}

      {/* Planning assumptions — written back to household profile */}
      <section className="card p-5 border border-blue/30 bg-blue/5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BookmarkCheck size={16} className="text-blue-light" aria-hidden />
            <h2 className="panel-title">
              Planning Assumptions
            </h2>
          </div>
          {profileSaved && (
            <span className="text-[10px] text-emerald-light font-semibold animate-fade-in">
              Saved to profile
            </span>
          )}
        </div>
        <p className="text-xs text-text-muted mb-4">
          These flow through to Monte Carlo, FIRE, and drawdown projections automatically.
        </p>

        <div className="grid sm:grid-cols-2 gap-5">
          {/* Toggle: include Farm Avenue sale */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-text-primary">
                Include Farm Avenue sale in projections
              </p>
              <p className="text-[10px] text-text-muted mt-0.5">
                Injects ~£311k net proceeds into your liquid pot
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={includeFarmAvenueSale ? 'true' : 'false'}
              aria-label="Include Farm Avenue sale in projections"
              title="Include Farm Avenue sale in projections"
              onClick={() => {
                const next = !includeFarmAvenueSale
                setIncludeFarmAvenueSale(next)
                debouncedProfileUpdate(next, farmAvenueSaleYear)
              }}
              className={clsx(
                'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent',
                'transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue',
                includeFarmAvenueSale ? 'bg-blue' : 'bg-surface-hover',
              )}
            >
              <span
                className={clsx(
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-text-primary shadow ring-0 transition duration-200 ease-in-out',
                  includeFarmAvenueSale ? 'translate-x-5' : 'translate-x-0',
                )}
              />
            </button>
          </div>

          {/* Slider: sale year */}
          <div className={clsx(!includeFarmAvenueSale && 'opacity-40 pointer-events-none')}>
            <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
              Sale year
            </label>
            <input
              type="range"
              min={2026}
              max={2030}
              step={1}
              value={farmAvenueSaleYear}
              disabled={!includeFarmAvenueSale}
              onChange={(e) => {
                const yr = parseInt(e.target.value, 10)
                setFarmAvenueSaleYear(yr)
                debouncedProfileUpdate(includeFarmAvenueSale, yr)
              }}
              className="w-full accent-blue"
              aria-label="Farm Avenue sale year"
            />
            <p className="text-xs font-bold text-text-primary">{farmAvenueSaleYear}</p>
          </div>
        </div>
      </section>

      {/* Scenario 1: Sell Farm Avenue */}
      {farmAvenue && (
        <section className="card p-5 border-2 border-rose/30 bg-rose/5">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle size={18} className="text-rose-light shrink-0 mt-0.5" aria-hidden />
            <div>
              <h2 className="text-lg font-bold text-text-primary">
                Sell 35 Farm Avenue
              </h2>
              <p className="text-xs text-text-muted mt-1">
                Urgent — empty BTL bleeding mortgage interest
              </p>
            </div>
          </div>

          <p className="text-sm text-text-primary mb-5">{farmAvenue.summary}</p>

          {/* Inputs */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
                Sale price
              </label>
              <input
                type="range"
                min={500_000}
                max={750_000}
                step={5_000}
                value={salePrice}
                onChange={(e) => setSalePrice(parseFloat(e.target.value))}
                className="w-full accent-blue"
                aria-label="Sale price"
              />
              <p className="text-xs font-bold text-text-primary">{fmt(salePrice)}</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
                Purchase price (2013)
              </label>
              <input
                type="range"
                min={300_000}
                max={550_000}
                step={5_000}
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(parseFloat(e.target.value))}
                className="w-full accent-blue"
                aria-label="Purchase price"
              />
              <p className="text-xs font-bold text-text-primary">{fmt(purchasePrice)}</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
                Improvements / costs (£)
              </label>
              <input
                type="range"
                min={0}
                max={150_000}
                step={2_500}
                value={improvements}
                onChange={(e) => setImprovements(parseFloat(e.target.value))}
                className="w-full accent-blue"
                aria-label="Improvements"
              />
              <p className="text-xs font-bold text-text-primary">{fmt(improvements)}</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
                Sale costs %
              </label>
              <input
                type="range"
                min={0.5}
                max={5.0}
                step={0.25}
                value={costsPct}
                onChange={(e) => setCostsPct(parseFloat(e.target.value))}
                className="w-full accent-blue"
                aria-label="Sale costs pct"
              />
              <p className="text-xs font-bold text-text-primary">{costsPct.toFixed(2)}%</p>
            </div>
          </div>

          {/* PRR + CGT breakdown panel */}
          <div className="card p-4 mb-5 bg-blue/5 border-blue/20">
            <p className="text-xs font-semibold text-text-primary mb-2 flex items-center gap-2">
              <TrendingUp size={12} className="text-blue-light" aria-hidden />
              Private Residence Relief breakdown
            </p>
            <p className="text-[11px] text-text-muted mb-3">
              You lived at 35 Farm Avenue as your main home from {farmAvenue.sale_mechanics.ownership_start} to{' '}
              {farmAvenue.sale_mechanics.main_residence_end}, then let it. PRR exempts the gain for
              the live-in period + final 9 months.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
              <div>
                <p className="text-text-muted">Total months owned</p>
                <p className="text-text-primary font-bold font-mono">{farmAvenue.sale_mechanics.total_months_owned}</p>
              </div>
              <div>
                <p className="text-text-muted">PRR-eligible months</p>
                <p className="text-emerald-light font-bold font-mono">{farmAvenue.sale_mechanics.prr_eligible_months}</p>
              </div>
              <div>
                <p className="text-text-muted">PRR exempt fraction</p>
                <p className="text-emerald-light font-bold font-mono">{farmAvenue.sale_mechanics.prr_fraction_pct}%</p>
              </div>
              <div>
                <p className="text-text-muted">Chargeable fraction</p>
                <p className="text-amber-light font-bold font-mono">{farmAvenue.sale_mechanics.chargeable_fraction_pct}%</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border space-y-1 text-[11px]">
              <div className="flex justify-between">
                <span className="text-text-muted">Gross gain</span>
                <span className="font-mono text-text-primary">{fmt(farmAvenue.sale_mechanics.gross_gain_gbp)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Less PRR exempt</span>
                <span className="font-mono text-emerald-light">−{fmt(farmAvenue.sale_mechanics.prr_exempt_gain_gbp)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Chargeable gain</span>
                <span className="font-mono text-text-primary">{fmt(farmAvenue.sale_mechanics.chargeable_gain_gbp)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Less CGT allowance (joint)</span>
                <span className="font-mono text-emerald-light">−{fmt(farmAvenue.sale_mechanics.cgt_allowance_gbp)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Taxable gain</span>
                <span className="font-mono text-text-primary">{fmt(farmAvenue.sale_mechanics.taxable_gain_gbp)}</span>
              </div>
              <div className="flex justify-between border-t border-border pt-1 mt-1">
                <span className="text-text-muted">CGT due @ {farmAvenue.sale_mechanics.cgt_rate_pct}%</span>
                <span className="font-mono font-bold text-rose-light">{fmt(farmAvenue.sale_mechanics.cgt_due_gbp)}</span>
              </div>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5 stagger-children">
            <div className="stat-card">
              <p className="stat-label">Cash freed (net)</p>
              <p className="stat-hero text-emerald-light">{fmt(farmAvenue.sale_mechanics.cash_after_cgt_gbp)}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Mortgage redeemed</p>
              <p className="stat-value">{fmt(farmAvenue.sale_mechanics.mortgage_redeemed_gbp)}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">FIRE multiple</p>
              <p className="stat-value">
                {farmAvenue.fire_impact.multiple_before.toFixed(1)}×
                <ArrowRight className="inline mx-1" size={14} aria-hidden />
                {farmAvenue.fire_impact.multiple_after.toFixed(1)}×
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Annual cashflow swing</p>
              <p className="stat-value text-emerald-light">
                +{fmt(farmAvenue.cashflow_impact.total_cashflow_swing_gbp_yr)}/yr
              </p>
              <p className="text-[10px] text-text-muted mt-0.5">
                {fmt(farmAvenue.cashflow_impact.mortgage_interest_saved_gbp_yr)} interest saved + {fmt(farmAvenue.cashflow_impact.extra_yield_on_freed_cash_gbp_yr)} yield on deployed cash
              </p>
            </div>
          </div>

          {/* Before/After table */}
          <div className="card p-4 bg-surface-DEFAULT/50">
            <h3 className="panel-title mb-2">
              Before vs After
            </h3>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-text-muted">Net worth</span>
                <span className="font-mono text-text-primary">
                  {fmt(farmAvenue.household_impact.net_worth_before_gbp)}
                  <ArrowRight className="inline mx-1" size={10} aria-hidden />
                  {fmt(farmAvenue.household_impact.net_worth_after_gbp)}
                  <span className={clsx('ml-2 font-semibold', farmAvenue.household_impact.net_worth_delta_gbp >= 0 ? 'text-emerald-light' : 'text-rose-light')}>
                    ({farmAvenue.household_impact.net_worth_delta_gbp >= 0 ? '+' : ''}{fmt(farmAvenue.household_impact.net_worth_delta_gbp)})
                  </span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Tracked investments</span>
                <span className="font-mono text-text-primary">
                  {fmt(farmAvenue.household_impact.tracked_before_gbp)}
                  <ArrowRight className="inline mx-1" size={10} aria-hidden />
                  {fmt(farmAvenue.household_impact.tracked_after_gbp)}
                  <span className="ml-2 text-emerald-light font-semibold">
                    (+{fmt(farmAvenue.household_impact.tracked_delta_gbp)})
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-5">
            <h3 className="panel-title mb-2">
              Action plan
            </h3>
            <ol className="space-y-2 text-xs text-text-secondary">
              {farmAvenue.actions.map((a, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-blue-light font-bold shrink-0">{i + 1}.</span>
                  <span>{a}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* Scenario 2: Sell The Gables & Rent Abroad */}
      {gablesRelocate && (
        <section className="card p-5 border-2 border-emerald/30 bg-emerald/5">
          <div className="flex items-start gap-3 mb-4">
            <Home size={18} className="text-emerald-light shrink-0 mt-0.5" aria-hidden />
            <div>
              <h2 className="text-lg font-bold text-text-primary">
                Sell The Gables &amp; Rent Abroad
              </h2>
              <p className="text-xs text-text-muted mt-1">
                Conditional on leaving the UK — models the property sale + renting overseas over a long horizon
              </p>
            </div>
          </div>

          <p className="text-sm text-text-primary mb-5">{gablesRelocate.summary}</p>

          {/* Sliders */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
                Sale price
              </label>
              <input
                type="range"
                min={1_200_000}
                max={2_000_000}
                step={25_000}
                value={gablesSalePrice}
                onChange={(e) => setGablesSalePrice(parseFloat(e.target.value))}
                className="w-full accent-blue"
                aria-label="Gables sale price"
              />
              <p className="text-xs font-bold text-text-primary">{fmt(gablesSalePrice)}</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
                Monthly rent abroad
              </label>
              <input
                type="range"
                min={2_000}
                max={8_000}
                step={100}
                value={gablesMonthlyRent}
                onChange={(e) => setGablesMonthlyRent(parseFloat(e.target.value))}
                className="w-full accent-blue"
                aria-label="Monthly rent abroad"
              />
              <p className="text-xs font-bold text-text-primary">{fmt(gablesMonthlyRent)}/mo</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
                Years renting
              </label>
              <input
                type="range"
                min={10}
                max={30}
                step={1}
                value={gablesYearsRenting}
                onChange={(e) => setGablesYearsRenting(parseFloat(e.target.value))}
                className="w-full accent-blue"
                aria-label="Years renting"
              />
              <p className="text-xs font-bold text-text-primary">{gablesYearsRenting} yrs</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
                Deployed yield %
              </label>
              <input
                type="range"
                min={2}
                max={8}
                step={0.1}
                value={gablesDeployedYield}
                onChange={(e) => setGablesDeployedYield(parseFloat(e.target.value))}
                className="w-full accent-blue"
                aria-label="Deployed yield pct"
              />
              <p className="text-xs font-bold text-text-primary">{gablesDeployedYield.toFixed(1)}%</p>
            </div>
          </div>

          {/* PRR callout */}
          <div className="card p-4 mb-5 bg-emerald/5 border border-emerald/30">
            <p className="text-xs font-semibold text-emerald-light mb-1 flex items-center gap-2">
              <span className="badge badge-green text-[10px]">100% PRR</span>
              100% Private Residence Relief — Zero CGT
            </p>
            <p className="text-[11px] text-text-secondary">{gablesRelocate.sale_mechanics.note}</p>
          </div>

          {/* Equity freed KPI */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5 stagger-children">
            <div className="stat-card border-emerald/30 bg-emerald/5">
              <p className="stat-label">Equity freed</p>
              <p className="stat-value text-emerald-light">{fmt(gablesRelocate.sale_mechanics.equity_freed_gbp)}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Mortgage redeemed</p>
              <p className="stat-value">{fmt(gablesRelocate.sale_mechanics.mortgage_redeemed_gbp)}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Sale costs</p>
              <p className="stat-value text-rose-light">−{fmt(gablesRelocate.sale_mechanics.sale_costs_gbp)}</p>
              <p className="text-[10px] text-text-muted mt-0.5">{gablesRelocate.sale_mechanics.costs_pct}% of sale price</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Lifetime rent (indexed)</p>
              <p className="stat-value text-rose-light">−{fmt(gablesRelocate.rent_abroad.lifetime_rent_gbp)}</p>
              <p className="text-[10px] text-text-muted mt-0.5">over {gablesRelocate.rent_abroad.years} yrs @ {gablesRelocate.rent_abroad.indexation_pct}% inflation</p>
            </div>
          </div>

          {/* 20-year cashflow breakdown panel */}
          <div className="card p-4 mb-5 bg-blue/5 border-blue/20">
            <p className="text-xs font-semibold text-text-primary mb-3 flex items-center gap-2">
              <TrendingUp size={12} className="text-blue-light" aria-hidden />
              {gablesRelocate.cashflow_over_horizon.years}-Year Cashflow Breakdown
            </p>
            <div className="space-y-1.5 text-[11px]">
              <div className="flex justify-between">
                <span className="text-text-muted">Yield on freed equity ({gablesDeployedYield.toFixed(1)}%)</span>
                <span className="font-mono text-emerald-light">+{fmt(gablesRelocate.cashflow_over_horizon.yield_on_freed_equity_gbp)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Mortgage payments saved</span>
                <span className="font-mono text-emerald-light">+{fmt(gablesRelocate.cashflow_over_horizon.mortgage_payments_saved_gbp)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Rent paid abroad</span>
                <span className="font-mono text-rose-light">−{fmt(gablesRelocate.cashflow_over_horizon.rent_paid_gbp)}</span>
              </div>
              <div className="flex justify-between border-t border-border pt-2 mt-2">
                <span className="font-semibold text-text-primary">Net swing</span>
                <span className={clsx(
                  'font-mono font-bold',
                  gablesRelocate.cashflow_over_horizon.net_swing_gbp >= 0 ? 'text-emerald-light' : 'text-rose-light',
                )}>
                  {gablesRelocate.cashflow_over_horizon.net_swing_gbp >= 0 ? '+' : ''}{fmt(gablesRelocate.cashflow_over_horizon.net_swing_gbp)}
                </span>
              </div>
            </div>
          </div>

          {/* Household impact grid */}
          <div className="card p-4 mb-5 bg-surface-DEFAULT/50">
            <h3 className="panel-title mb-2">
              Household Impact
            </h3>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-text-muted">Net worth</span>
                <span className="font-mono text-text-primary">
                  {fmt(gablesRelocate.household_impact.net_worth_before_gbp)}
                  <ArrowRight className="inline mx-1" size={10} aria-hidden />
                  {fmt(gablesRelocate.household_impact.net_worth_after_gbp)}
                  <span className={clsx('ml-2 font-semibold', gablesRelocate.household_impact.net_worth_delta_gbp >= 0 ? 'text-emerald-light' : 'text-rose-light')}>
                    ({gablesRelocate.household_impact.net_worth_delta_gbp >= 0 ? '+' : ''}{fmt(gablesRelocate.household_impact.net_worth_delta_gbp)})
                  </span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Tracked investments</span>
                <span className="font-mono text-text-primary">
                  {fmt(gablesRelocate.household_impact.tracked_before_gbp)}
                  <ArrowRight className="inline mx-1" size={10} aria-hidden />
                  {fmt(gablesRelocate.household_impact.tracked_after_gbp)}
                  <span className={clsx('ml-2 font-semibold', gablesRelocate.household_impact.tracked_delta_gbp >= 0 ? 'text-emerald-light' : 'text-rose-light')}>
                    ({gablesRelocate.household_impact.tracked_delta_gbp >= 0 ? '+' : ''}{fmt(gablesRelocate.household_impact.tracked_delta_gbp)})
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Verdict card */}
          <div className={clsx(
            'card p-4 border-2 mb-5',
            gablesRelocate.recommendation === 'POSITIVE'
              ? 'border-emerald/30 bg-emerald/5'
              : 'border-rose/30 bg-rose/5',
          )}>
            <p className={clsx(
              'text-xs font-semibold uppercase tracking-wider mb-2',
              gablesRelocate.recommendation === 'POSITIVE' ? 'text-emerald-light' : 'text-rose-light',
            )}>
              Verdict: {gablesRelocate.recommendation}
            </p>
            <p className="text-sm text-text-secondary">{gablesRelocate.summary}</p>
          </div>

          {/* Action plan */}
          <div className="mb-5">
            <h3 className="panel-title mb-2">
              Action plan
            </h3>
            <ol className="space-y-2 text-xs text-text-secondary">
              {gablesRelocate.actions.map((a, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-emerald-light font-bold shrink-0">{i + 1}.</span>
                  <span>{a}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Strategic callout linking to Jurisdictions */}
          <div className="card p-4 border border-amber/30 bg-amber/5">
            <p className="text-xs text-amber-light font-semibold mb-1">
              ⚠ This scenario ignores the real relocation prize
            </p>
            <p className="text-xs text-text-secondary">
              Selling The Gables and renting abroad frees up equity and eliminates mortgage costs — but the
              headline prize is the <strong>£2.5M+ in tax &amp; IHT savings</strong> available in the right
              jurisdiction. The figures above do not include those savings.{' '}
              <Link to="/app/jurisdictions" className="text-blue-light underline hover:text-blue font-semibold">
                See the Jurisdictions page for the full picture →
              </Link>
            </p>
          </div>
        </section>
      )}

      {/* Scenario 3: Pay off The Gables */}
      {gables && (
        <section className="card p-5">
          <div className="flex items-start gap-3 mb-4">
            <TrendingUp size={18} className="text-amber shrink-0 mt-0.5" aria-hidden />
            <div>
              <h2 className="text-lg font-bold text-text-primary">
                Pay off The Gables mortgage early?
              </h2>
              <p className="text-xs text-text-muted mt-1">
                2.7% fixed until Nov 2029 — is early repayment worth it?
              </p>
            </div>
          </div>

          <p className="text-sm text-text-primary mb-5">{gables.summary}</p>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div className="card p-3 bg-surface-DEFAULT/50">
              <p className="text-[10px] uppercase tracking-wider text-text-muted">Annual interest saved</p>
              <p className="text-lg font-bold text-emerald-light mt-1">
                +{fmt(gables.current_state.annual_interest_gbp)}/yr
              </p>
            </div>
            <div className="card p-3 bg-surface-DEFAULT/50">
              <p className="text-[10px] uppercase tracking-wider text-text-muted">Opportunity cost forgone</p>
              <p className="text-lg font-bold text-rose-light mt-1">
                −{fmt(gables.opportunity_cost.annual_opportunity_cost_gbp)}/yr
              </p>
            </div>
          </div>

          <div className={clsx(
            'card p-4 border-2',
            gables.verdict.net_annual_saving_gbp < 0
              ? 'border-rose/30 bg-rose/5'
              : 'border-emerald/30 bg-emerald/5',
          )}>
            <p className="panel-title mb-2">
              Verdict: {gables.verdict.recommendation}
            </p>
            <p className="text-xs text-text-secondary mb-2">{gables.verdict.reasoning}</p>
            <p className={clsx(
              'text-sm font-bold',
              gables.verdict.net_annual_saving_gbp < 0 ? 'text-rose-light' : 'text-emerald-light',
            )}>
              Net annual impact: {gables.verdict.net_annual_saving_gbp >= 0 ? '+' : ''}{fmt(gables.verdict.net_annual_saving_gbp)}/yr
            </p>
            <p className="text-[10px] text-text-muted mt-2">
              Plus a potential early-repayment charge of {fmt(gables.early_repayment_charge.low_gbp)} – {fmt(gables.early_repayment_charge.high_gbp)}.
            </p>
          </div>
        </section>
      )}
    </div>
  )
}
